'use client'

import { Chart } from '@components/common/Chart'
import UIFlex from '@components/ui/UIFlex'
import UIText from '@components/ui/UIText'
import { Box, styled } from '@mui/joy'
import color from '@ui/style/color.theme'
import { pretendard, pxToRem } from '@ui/style/muiTheme'
import Icon14CheckCircle from 'src/assets/icons/Icon14CheckCircle'
import {
	PollResultSetProps,
	computeAnswerList,
	getPercent,
	getTotal,
	getVote,
	remainPercent,
} from '.'

const ColorChip = styled(Box)(() => ({
	width: pxToRem(14),
	height: pxToRem(14),
	borderRadius: pxToRem(1),
	marginRight: pxToRem(4),
}))

const chipBgColor = [color.colDarkBlue, color.colLightBlue]
const dataLabelColor = [color.colWhite, color.colDarkBlue]
const PollResultPieSet = ({ poll, selected }: PollResultSetProps) => {
	const answerList = computeAnswerList(poll.answerList)
	const total = getTotal(answerList)

	return (
		<UIFlex
			padding={`${pxToRem(24)} ${pxToRem(16)}`}
			borderRadius={pxToRem(8)}
			bgcolor={color.colWhite}
		>
			<UIFlex
				sx={{
					'&>*:not(:first-child)': {
						marginTop: pxToRem(20),
					},
				}}
			>
				<UIFlex
					alignItems="center"
					sx={{
						height: pxToRem(160),
						'& .highcharts-figure .count': {
							color: '#8296ad',
						},
						'& .highcharts-label span': {
							fontFamily: `${pretendard.style.fontFamily}!important`,
							fontSize: {
								xs: `${pxToRem(13)}!important`,
							},
							lineHeight: {
								xs: `${pxToRem(18)}!important`,
							},
							fontWeight: `${600}!important`,
						},
					}}
				>
					<figure className="highcharts-figure">
						<Chart
							options={{
								chart: {
									type: 'pie',
									width: 160,
									height: 160,
									backgroundColor: 'transparent',
									margin: -10,
								},
								title: {
									text: '',
								},
								tooltip: {
									enabled: false,
								},
								accessibility: {
									enabled: false,
								},
								plotOptions: {
									pie: {
										innerSize: '0%',
										startAngle: 0,
										dataLabels: {
											enabled: true,
											animation: true,
											format: `{point.percentage:.0f}%<br><span class="count">{point.count}</span>`,
											distance: -40,
											useHTML: true,
											style: {
												textOutline: 'none',
												textAlign: 'center',
											},
										},
										states: {
											hover: {
												enabled: true,
												brightness: 0.05,
												halo: null,
											},
											inactive: {
												opacity: 1,
											},
										},
										borderWidth: 0,
										borderRadius: 0,
									},
								},
								series: [
									{
										type: 'pie',
										data: answerList.map((answer, index) => ({
											count: getVote(answer.answerPollCnt),
											y:
												answerList.length - 1 === index && total !== 0
													? remainPercent(answerList, total)
													: getPercent(answer.answerPollCnt, total),
											color: chipBgColor[index],
											dataLabels: {
												color: dataLabelColor[index],
											},
										})),
									},
								],
							}}
						/>
					</figure>
				</UIFlex>
				<UIFlex
					alignItems="center"
					sx={{
						'&>*:not(:first-child)': {
							marginTop: pxToRem(4),
						},
					}}
				>
					{answerList.map((answer, index) => (
						<UIFlex
							flexDirection="row"
							alignItems="center"
							key={`answer-${answer.answerIdx}`}
							sx={{
								'&>*:not(:first-child)': {
									marginLeft: pxToRem(4),
								},
							}}
						>
							<ColorChip bgcolor={chipBgColor[index] ?? color.colGray5} />
							<UIText
								fontSize={pxToRem(14)}
								lineHeight={pxToRem(20)}
								fontWeight={500}
							>
								{answer.answerContent}
							</UIText>
							{(answer.isUserSelect ||
								selected?.includes(answer.answerIdx.toString())) && (
								<Icon14CheckCircle />
							)}
						</UIFlex>
					))}
				</UIFlex>
			</UIFlex>
		</UIFlex>
	)
}

export default PollResultPieSet
